// UI animation helpers with personality (§11.5): count-ups, staggered pops, DOM confetti bursts
// and the screen flash. All respect Reduced Motion / Reduced Flashing.
import { el, reducedMotion } from './ui';

/** Tweens a number shown in `node` from `from` to `to` over `ms` (ease-out), calling `tick` per step. */
export function countUp(node: HTMLElement, from: number, to: number, ms: number, fmt: (n: number) => string = (n) => String(Math.round(n)), tick?: (n: number) => void): () => void {
  if (reducedMotion() || ms <= 0 || from === to) {
    node.textContent = fmt(to);
    return () => undefined;
  }
  const start = performance.now();
  let raf = 0;
  const step = (t: number): void => {
    const k = Math.min(1, (t - start) / ms);
    const e = 1 - (1 - k) ** 3;
    const v = from + (to - from) * e;
    node.textContent = fmt(v);
    tick?.(v);
    if (k < 1) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

const CONFETTI_SHAPES = ['50%', '0.15em', '50% 0'] as const;

/**
 * Bursts `count` confetti pieces from (x, y) in `host` (CSS px relative to host) in the given
 * colors. Pieces remove themselves. Skipped entirely with Reduced Motion.
 */
export function confetti(host: HTMLElement, x: number, y: number, colors: readonly string[], count = 40, spread = 1): void {
  if (reducedMotion() || typeof host.animate !== 'function') return;
  const layer = el('div', { class: 'confetti-layer' });
  host.append(layer);
  let remaining = count;
  for (let i = 0; i < count; i++) {
    const size = 0.45 + Math.random() * 0.45;
    const piece = el('i', {
      class: 'confetti',
      style: {
        left: `${x}px`, top: `${y}px`, width: `${size}em`, height: `${size * (0.4 + Math.random() * 0.8)}em`,
        background: colors[i % colors.length], 'border-radius': CONFETTI_SHAPES[i % CONFETTI_SHAPES.length],
      },
    });
    layer.append(piece);
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.3 * spread;
    const speed = 160 + Math.random() * 260;
    const dx = Math.cos(angle) * speed;
    const dy = Math.sin(angle) * speed;
    const fall = 260 + Math.random() * 240;
    const spin = (Math.random() - 0.5) * 1440;
    const dur = 1100 + Math.random() * 700;
    const anim = piece.animate([
      { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
      { transform: `translate(${dx * 0.7}px, ${dy * 0.7}px) rotate(${spin * 0.5}deg)`, opacity: 1, offset: 0.35 },
      { transform: `translate(${dx}px, ${dy + fall}px) rotate(${spin}deg)`, opacity: 0 },
    ], { duration: dur, easing: 'cubic-bezier(0.15, 0.6, 0.4, 1)', fill: 'forwards' });
    anim.onfinish = () => {
      piece.remove();
      remaining--;
      if (remaining === 0) layer.remove();
    };
  }
}

/** Adds `cls` to each node with a growing delay (staggered pops). */
export function stagger(nodes: readonly HTMLElement[], stepMs: number, cls = 'pop', startMs = 0): void {
  nodes.forEach((n, i) => {
    n.style.animationDelay = `${startMs + i * stepMs}ms`;
    n.classList.add(cls);
  });
}

/** A brief full-screen white flash (§11.5 BONK!); skipped with Reduced Flashing. */
export function flash(host: HTMLElement, opacity = 0.15, ms = 60): void {
  if (document.getElementById('ui')?.classList.contains('reduced-flashing')) return;
  const f = el('div', { class: 'screen-flash', style: { opacity: String(opacity) } });
  host.append(f);
  setTimeout(() => f.remove(), ms);
}
